import React, { useEffect, useRef, useState } from 'react';
import { aboutData } from '../mockData';

export const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState(aboutData.stats.map(() => 0));
  const [scrollY, setScrollY] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          animateCounters();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const animateCounters = () => {
    aboutData.stats.forEach((stat, index) => {
      const target = parseInt(stat.number);
      const duration = 2000;
      const steps = 60;
      const increment = target / steps;
      let current = 0;

      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }
        setCounters(prev => {
          const newCounters = [...prev];
          newCounters[index] = Math.floor(current);
          return newCounters;
        });
      }, duration / steps);
    });
  };

  return (
    <section id="about" ref={sectionRef} className="py-24 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* 3D Parallax Background Elements */}
      <div 
        className="absolute top-20 right-10 w-96 h-96 bg-[#FF6B00]/10 rounded-full blur-3xl"
        style={{ transform: `translateY(${scrollY * 0.1}px)` }}
      ></div>
      <div 
        className="absolute bottom-20 left-10 w-[500px] h-[500px] bg-[#FF6B00]/5 rounded-full blur-3xl"
        style={{ transform: `translateY(${scrollY * -0.15}px)` }}
      ></div>
      
      {/* Animated Grid Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, #FF6B00 1px, transparent 0)',
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Title with enhanced styling */}
        <div className="text-center mb-16 animate-fade-in-up">
          <span className="inline-block text-[#FF6B00] uppercase tracking-wider text-sm font-semibold mb-4">Who We Are</span>
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            About <span className="gradient-text">Us</span>
          </h2>
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-gradient-to-r from-transparent to-[#FF6B00]"></div>
            <div className="w-24 h-1 bg-gradient-to-r from-[#FF6B00] to-[#E65F00]"></div>
            <div className="w-12 h-1 bg-gradient-to-r from-[#E65F00] to-transparent"></div>
          </div>
          <p className="text-[#666666] text-lg max-w-2xl mx-auto">
            Transforming spaces with precision and passion since 2018
          </p>
        </div>

        {/* Two Column Layout with enhanced animations */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          {/* Image Column with enhanced effects */}
          <div className="relative group animate-fade-in-left animation-delay-200">
            <div className="absolute -inset-4 bg-gradient-to-r from-[#FF6B00] to-[#E65F00] opacity-20 blur-xl group-hover:opacity-30 transition-opacity duration-500"></div>
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img
                src={aboutData.imageUrl}
                alt="Lalit Interior Showcase"
                className="w-full h-[500px] object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
              {/* Overlay with decorative elements */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              
              {/* Floating badge */}
              <div className="absolute top-6 left-6 bg-gradient-to-r from-[#FF6B00] to-[#E65F00] text-white px-6 py-4 rounded-2xl shadow-xl transform group-hover:scale-110 transition-transform duration-300">
                <p className="text-3xl font-bold">8+</p>
                <p className="text-sm">Years of Excellence</p>
              </div>

              {/* Decorative corner elements */}
              <div className="absolute top-0 right-0 w-20 h-20 border-t-4 border-r-4 border-white/30 rounded-tr-2xl"></div>
              <div className="absolute bottom-0 left-0 w-20 h-20 border-b-4 border-l-4 border-white/30 rounded-bl-2xl"></div>
            </div>
          </div>

          {/* Content Column with stagger animation */}
          <div className="space-y-6 animate-fade-in-right animation-delay-300">
            {aboutData.description.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-[#555555] text-justify leading-relaxed hover:text-[#333333] transition-colors duration-300">
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        {/* Stats Grid with 3D Card Effects */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 animate-fade-in-up animation-delay-400">
          {aboutData.stats.map((stat, index) => (
            <div
              key={index}
              className="stat-card-3d group relative bg-gradient-to-br from-white to-gray-50 p-8 rounded-3xl text-center shadow-xl transition-all duration-500 overflow-hidden border border-gray-200"
              onMouseMove={(e) => {
                const card = e.currentTarget;
                const rect = card.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                const centerX = rect.width / 2;
                const centerY = rect.height / 2;
                const rotateX = ((y - centerY) / centerY) * -10;
                const rotateY = ((x - centerX) / centerX) * 10;
                card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-10px) scale(1.05)`;
              }}
              onMouseLeave={(e) => {
                const card = e.currentTarget;
                card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px) scale(1)';
              }}
            >
              {/* Animated background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B00]/20 to-[#E65F00]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* 3D Floating Orb */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-[#FF6B00]/30 to-[#E65F00]/30 rounded-full blur-2xl group-hover:scale-150 group-hover:-translate-y-5 transition-all duration-700"></div>
              
              {/* Shine Effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute top-0 -left-full w-1/2 h-full bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:left-full transition-all duration-1000 ease-out"></div>
              </div>
              
              <div className="relative z-10">
                <p className="text-6xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-br from-[#FF6B00] to-[#E65F00] mb-4 group-hover:scale-110 transition-transform duration-300 drop-shadow-lg">
                  {isVisible ? (
                    index === 1 ? '2 Lakh+' :
                    index === 2 ? '150+' : 
                    index === 0 ? `${counters[index]}+` :
                    `${counters[index]}+`
                  ) : '0'}
                </p>
                <p className="text-sm font-bold text-gray-600 uppercase tracking-wider group-hover:text-[#FF6B00] transition-colors duration-300">
                  {stat.label}
                </p>

                {/* Bottom Accent Line */}
                <div className="mt-4 mx-auto w-0 h-1 bg-gradient-to-r from-[#FF6B00] to-[#E65F00] group-hover:w-full transition-all duration-500 rounded-full"></div>
              </div>

              {/* 3D Corner Accents */}
              <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-[#FF6B00]/20 rounded-tl-3xl group-hover:border-[#FF6B00] transition-colors duration-300"></div>
              <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-[#FF6B00]/20 rounded-br-3xl group-hover:border-[#FF6B00] transition-colors duration-300"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
